import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Labyrinth from './Labyrinth';
import { useLogo } from '../context/LogoContext';

const Hero: React.FC = () => {
  const [showShrivatsa, setShowShrivatsa] = useState(false);
  const [textPhaseIndex, setTextPhaseIndex] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { setLogoInHeader, logoClicked, setLogoClicked, setIsAnimating, setIsSrivatsaView } = useLogo();
  
  // Text phases for the labyrinth
  const [showLeftText, setShowLeftText] = useState(false);
  const [showRightText, setShowRightText] = useState(false);
  const [showBottomText, setShowBottomText] = useState(false);
  const [showTopText, setShowTopText] = useState(false);
  const [startRotation, setStartRotation] = useState(false);
  const [showSubstansLogo, setShowSubstansLogo] = useState(false);
  
  // Logo animation states
  const [logoAnimationComplete, setLogoAnimationComplete] = useState(false);
  
  // Calculate logo size based on viewport width and height for better mobile responsiveness
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [viewportSize, setViewportSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  
  // Responsive size calculations
  const labyrinthSize = isMobile 
    ? Math.min(viewportSize.width * 0.85, viewportSize.height * 0.5) // Better size constraint for mobile
    : Math.min(viewportSize.width * 0.6, 500) * 1.5;
  const logoSize = labyrinthSize * 0.1; // 10% of labyrinth size
  
  // Adjusted positioning for mobile to reduce the gap at the top
  const mobileTopOffset = isMobile ? '-25px' : '0'; // Reduce the gap at the top on mobile
  
  // Handle viewport resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Start the animation sequence with staggered timing
  useEffect(() => {
    // Set up all timeouts
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    
    // First text - left (celebrating essence)
    timeouts.push(setTimeout(() => {
      setShowLeftText(true);
    }, 500));
    
    // Second text - right (embracing paradox)
    timeouts.push(setTimeout(() => {
      setShowRightText(true);
    }, 2000));
    
    // Third text - bottom (united through love)
    timeouts.push(setTimeout(() => {
      setShowBottomText(true);
    }, 3500));
    
    // Fourth text - top (SUBSTANS)
    timeouts.push(setTimeout(() => {
      setShowTopText(true);
    }, 5000));
    
    // Start rotation after SUBSTANS title appears
    timeouts.push(setTimeout(() => {
      setStartRotation(true);
    }, 6000));
    
    // Show Substans logo with a 1-second delay after labyrinth starts rotating
    timeouts.push(setTimeout(() => {
      setShowSubstansLogo(true); // This will trigger the logo to fade in
    }, 7000));
    
    // Cleanup function to clear all timeouts when component unmounts
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  // Handle the logo click to trigger the animation sequence
  const handleLogoClick = () => {
    setLogoClicked(true); // Update the shared state
    setIsAnimating(true); // Set animation flag to prevent header logo from showing
    
    // After logo animation completes - smooth animation to header
    setTimeout(() => {
      setLogoAnimationComplete(true);
      setLogoInHeader(true); // Update the context to show logo in header
      setShowShrivatsa(true);
      setIsSrivatsaView(true); // Mark that we're now in the śrivatsa view
      setIsAnimating(false); // Animation is complete
    }, 1600); // Time for logo to move to corner (1600ms for smooth animation)
  };

  // Progress through text phases after Shrivatsa appears
  useEffect(() => {
    if (showShrivatsa) {
      const timer = setTimeout(() => {
        setAnimationComplete(true);
      }, 1200); // Wait for exit animation to complete

      return () => clearTimeout(timer);
    }
  }, [showShrivatsa]);

  // Cycle through text phrases
  useEffect(() => {
    if (animationComplete && textPhaseIndex < 3) {
      const timer = setTimeout(() => {
        setTextPhaseIndex(prevIndex => prevIndex + 1);
      }, 1800); // Time between text phrases appearing

      return () => clearTimeout(timer);
    }
  }, [animationComplete, textPhaseIndex]);

  // The new labyrinth design has the logo built-in, so we don't need a separate logo source

  return (
    <div className="relative h-screen bg-white flex items-center justify-center overflow-hidden">
      {/* Initial View with Alternative Labyrinth */}
      <div 
        className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${showShrivatsa ? 'opacity-0' : 'opacity-100'}`}
        style={{ marginTop: mobileTopOffset }} // Apply the top offset to reduce the gap
      >
        {/* Grid layout for labyrinth and text */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 items-center h-full" ref={containerRef}>
          {/* Left text - celebrating essence (hidden on mobile, shown on desktop) */}
          <div className="hidden md:flex justify-end items-center pr-24"> {/* Added another 10px padding (pr-20 to pr-24) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: showLeftText ? 1 : 0 }}
              transition={{ duration: 1 }}
              className="text-2xl font-ogg font-light text-black text-right"
            >
              celebrating essence
            </motion.div>
          </div>
          
          {/* Center column with labyrinth and top/bottom text */}
          <div className="flex flex-col items-center justify-center relative col-span-1 md:col-span-1">
            {/* Top logotype (SUBSTANS) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: showTopText ? 1 : 0 }}
              transition={{ duration: 1 }}
              className="mb-4 md:mb-8 flex justify-center items-center" /* Container for the logotype */
              style={{ height: '2rem', minHeight: '2rem' }} /* Maintain consistent height */
            >
              <img 
                src="/substans logotype.png" 
                alt="SUBSTANS" 
                className="h-full" /* Use height to maintain proper sizing */
                style={{ 
                  maxWidth: '160px', /* Control maximum width */
                  objectFit: 'contain', /* Ensure proper scaling */
                  filter: 'brightness(0)' /* Make the logotype black */
                }}
              />
            </motion.div>
            
            {/* Labyrinth */}
            <div className="relative">
              <Labyrinth 
                svgPath="/Labyrinth.svg"
                size={labyrinthSize} /* Use the responsive size calculation */
                onClick={handleLogoClick}
                rotate={startRotation} /* Start rotation after SUBSTANS title appears */
                showLogo={showSubstansLogo} /* Show logo with fade-in effect after delay */
                logoClicked={logoClicked} /* Pass the clicked state to animate the logo */
              />
              
              {/* Clickable overlay */}
              <div 
                className="absolute inset-0 cursor-pointer" 
                onClick={handleLogoClick}
              ></div>
            </div>
            
            {/* Bottom text - united through love (hidden on mobile, shown on desktop) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: showBottomText ? 1 : 0 }}
              transition={{ duration: 1 }}
              className="mt-4 md:mt-8 text-xl md:text-2xl font-ogg font-light text-black hidden md:block"
            >
              united through love
            </motion.div>
          </div>
          
          {/* Right text - embracing paradox (hidden on mobile, shown on desktop) */}
          <div className="hidden md:flex justify-start items-center pl-24"> {/* Added another 10px padding (pl-20 to pl-24) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: showRightText ? 1 : 0 }}
              transition={{ duration: 1 }}
              className="text-2xl font-ogg font-light text-black text-left"
            >
              embracing paradox
            </motion.div>
          </div>
          
          {/* Add custom CSS for mobile text positioning */}
          <style dangerouslySetInnerHTML={{ __html: `
            @media (max-width: 767px) {
              .mobile-text-container {
                position: absolute !important;
                top: auto !important;
                bottom: 5% !important; /* Position further down from bottom */
                left: 0 !important;
                right: 0 !important;
                margin-top: 0 !important;
                z-index: 10 !important;
              }
            }
          `}} />
          
          {/* Mobile-only text container (shows all three phrases stacked under the labyrinth) */}
          <div className="flex flex-col items-center justify-center space-y-3 md:hidden mobile-text-container">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: showLeftText ? 1 : 0 }}
              transition={{ duration: 1 }}
              className="text-lg font-ogg font-light text-black text-center"
            >
              celebrating essence
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: showRightText ? 1 : 0 }}
              transition={{ duration: 1 }}
              className="text-lg font-ogg font-light text-black text-center"
            >
              embracing paradox
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: showBottomText ? 1 : 0 }}
              transition={{ duration: 1 }}
              className="text-lg font-ogg font-light text-black text-center"
            >
              united through love
            </motion.div>
          </div>
        </div>
      </div>



      {/* Animated Logo that travels from center to header and stays visible */}
      {logoClicked && (
        <Link 
          to="/"
          className="fixed z-50"
          style={{
            top: logoAnimationComplete ? '1rem' : '50%',
            left: logoAnimationComplete ? '1rem' : '50%',
            transform: 'translate(-50%, -50%)', // Always centered before animation
            transformOrigin: 'center',
            animation: logoAnimationComplete ? 'moveToHeader 1600ms forwards' : 'none',
            width: logoAnimationComplete ? '32px' : logoSize, // 32px (w-8) in header to match Navigation
            height: logoAnimationComplete ? '32px' : logoSize,
            pointerEvents: logoAnimationComplete ? 'auto' : 'none' // Only clickable after animation completes
          }}
        >
          <img 
            src="/substans.svg" 
            alt="Substans Logo" 
            className="w-full h-full"
          />
        </Link>
      )}

      {/* Shrivatsa Symbol View (appears after logo animation) - positioned exactly where the Substans logo was */}
      <div
        className={`absolute inset-0 w-full h-full flex items-center justify-center transition-opacity duration-1000 ${
          showShrivatsa && logoAnimationComplete ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <img
          src="/srivatsa.svg"
          alt="Śrivatsa Symbol"
          className="w-[calc(100vw-80px)] h-[calc(100vw-80px)] sm:w-80 sm:h-80 md:w-96 md:h-96 lg:w-[36rem] lg:h-[36rem]"
          style={{ 
            transform: isMobile ? 'translateY(-40px)' : 'translateY(-20px)' 
          }} /* Position higher on mobile to create space for text */
        />
      </div>
      
      {/* CSS Animation for logo movement - added to the head */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes moveToHeader {
          0% {
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
          100% {
            top: 1rem;
            left: 1rem;
            transform: translate(0, 0);
          }
        }
      `}} />

      {/* Text Phases (appear after Shrivatsa) - positioned below the śrivatsa symbol with better spacing */}
      <div
        className={`absolute inset-0 flex flex-col items-center justify-start transition-opacity duration-1000 ${
          animationComplete ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          paddingTop: isMobile ? 'calc(50vh + 120px)' : 'calc(50vh + 180px)', // Adjusted gap for mobile for better balance
          marginTop: isMobile ? '-20px' : '0', // Fine-tuned margin for better positioning
        }}
      >
        <div className="space-y-5 md:space-y-6 w-full px-6 text-center">
          <div className={`transition-opacity duration-1000 ${textPhaseIndex >= 0 ? 'opacity-100' : 'opacity-0'}`}>
            <p className="text-base sm:text-lg md:text-lg lg:text-xl font-book-antiqua transition-opacity duration-1000 text-center">
              our purpose is in the art of sense-making
            </p>
          </div>
          
          <div className={`transition-opacity duration-1000 ${textPhaseIndex >= 1 ? 'opacity-100' : 'opacity-0'}`}>
            <p className="text-base sm:text-lg md:text-lg lg:text-xl font-book-antiqua transition-opacity duration-1000 text-center">
              locality meets infinity – physical meets digital – wisdom meets compassion
            </p>
          </div>
          
          <div className={`transition-opacity duration-1000 ${textPhaseIndex >= 2 ? 'opacity-100' : 'opacity-0'}`}>
            <p className="text-base sm:text-lg md:text-lg lg:text-xl font-book-antiqua transition-opacity duration-1000 text-center">
              cultivating clarity, meaningful connection and profound consciousness within our commUnity
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
